const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:3001')
  : '';

export const API_ENDPOINTS = {
  config: {
    public: `${API_BASE_URL}/api/config/public`,
  },
  auth: {
    requestToken: `${API_BASE_URL}/api/auth/request-token`,
    verifyToken: `${API_BASE_URL}/api/auth/verify-token`,
    verifyCode: `${API_BASE_URL}/api/auth/verify-code`,
    me: `${API_BASE_URL}/api/auth/me`,
    logout: `${API_BASE_URL}/api/auth/logout`,
    signup: `${API_BASE_URL}/api/auth/signup`,
  },
  users: {
    getUser: (userId: string) => `${API_BASE_URL}/api/users/${userId}`,
    getUserByUsername: (username: string) => `${API_BASE_URL}/api/users/by-username/${username}`,
    updateName: (userId: string) => `${API_BASE_URL}/api/users/${userId}/name`,
    updateDisplayName: (userId: string) => `${API_BASE_URL}/api/users/${userId}/display-name`,
    updatePhone: (userId: string) => `${API_BASE_URL}/api/users/${userId}/phone`,
    updateBuilding: (userId: string) => `${API_BASE_URL}/api/users/${userId}/building`,
    followedOrder: (userId: string) => `${API_BASE_URL}/api/users/${userId}/followed-order`,
    uploadAvatar: (userId: string) => `${API_BASE_URL}/api/users/${userId}/avatar`,
    delete: (userId: string) => `${API_BASE_URL}/api/users/${userId}`,
    search: `${API_BASE_URL}/api/users/search`,
  },
  posts: {
    getAll: `${API_BASE_URL}/api/posts`,
    popularTags: `${API_BASE_URL}/api/posts/popular-tags`,
    getByTag: (tag: string) => `${API_BASE_URL}/api/posts?tag=${encodeURIComponent(tag)}`,
    getById: (postId: string) => `${API_BASE_URL}/api/post/${postId}`,
    getUserPosts: (userId: string) => `${API_BASE_URL}/api/posts/${userId}`,
    create: `${API_BASE_URL}/api/post/create`,
    edit: `${API_BASE_URL}/api/post/edit`,
    editIncident: (postId: string) => `${API_BASE_URL}/api/post/${postId}/incident`,
    delete: `${API_BASE_URL}/api/post/delete`,
    like: `${API_BASE_URL}/api/post/like`,
    comment: `${API_BASE_URL}/api/post/comment`,
    flag: (postId: string) => `${API_BASE_URL}/api/posts/${postId}/flag`,
    vote: (postId: string) => `${API_BASE_URL}/api/post/${postId}/poll/vote`,
    unpublish: (postId: string) => `${API_BASE_URL}/api/post/${postId}/unpublish`,
    republish: (postId: string) => `${API_BASE_URL}/api/post/${postId}/republish`,
    pin: (postId: string) => `${API_BASE_URL}/api/post/${postId}/pin`,
    deleteImage: (postId: string) => `${API_BASE_URL}/api/post/${postId}/image`,
    deleteDocument: (postId: string) => `${API_BASE_URL}/api/post/${postId}/document`,
  },
  comments: {
    reply: `${API_BASE_URL}/api/comment/reply`,
    like: `${API_BASE_URL}/api/comment/like`,
    delete: `${API_BASE_URL}/api/comment/delete`,
    edit: `${API_BASE_URL}/api/comment/edit`,
    flag: (commentId: string) => `${API_BASE_URL}/api/comment/${commentId}/flag`,
  },
  replies: {
    reply: `${API_BASE_URL}/api/reply/reply`,
    like: `${API_BASE_URL}/api/reply/like`,
    delete: `${API_BASE_URL}/api/reply/delete`,
    flag: (replyId: string) => `${API_BASE_URL}/api/reply/${replyId}/flag`,
  },
  buildings: {
    getAll: `${API_BASE_URL}/api/buildings`,
    getById: (id: string, userId?: string) =>
      `${API_BASE_URL}/api/buildings/${id}${userId ? `?userId=${userId}` : ''}`,
    nearby: `${API_BASE_URL}/api/buildings/nearby`,
    search: `${API_BASE_URL}/api/buildings/search`,
    create: `${API_BASE_URL}/api/buildings/create`,
    randomContent: `${API_BASE_URL}/api/buildings/random-content`,
    featured: `${API_BASE_URL}/api/buildings/featured`,
    reviews: (id: string, requestingUserId?: string) =>
      `${API_BASE_URL}/api/buildings/${id}/reviews${requestingUserId ? `?requestingUserId=${requestingUserId}` : ''}`,
    posts: (id: string) => `${API_BASE_URL}/api/buildings/${id}/posts`,
    tags: (id: string) => `${API_BASE_URL}/api/buildings/${id}/tags`,
    follow: (id: string) => `${API_BASE_URL}/api/buildings/${id}/follow`,
    followed: (userId: string) => `${API_BASE_URL}/api/buildings/followed/${userId}`,
    updateName: (id: string) => `${API_BASE_URL}/api/buildings/${id}/name`,
    strataMetadata: (id: string) => `${API_BASE_URL}/api/buildings/${id}/strata-metadata`,
    accessRequests: (id: string) => `${API_BASE_URL}/api/buildings/${id}/access-requests`,
    claims: (id: string) => `${API_BASE_URL}/api/buildings/${id}/claims`,
    approveClaim: (buildingId: string, claimId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/claims/${claimId}/approve`,
    rejectClaim: (buildingId: string, claimId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/claims/${claimId}/reject`,
    accessRequestDecision: (buildingId: string, userId: string) =>
      `${API_BASE_URL}/api/buildings/${buildingId}/access-requests/${userId}`,
    operators: (id: string) => `${API_BASE_URL}/api/buildings/${id}/operators`,
    operatorEntry: (buildingId: string, userId: string) =>
      `${API_BASE_URL}/api/buildings/${buildingId}/operators/${userId}`,
    contacts: (id: string) => `${API_BASE_URL}/api/buildings/${id}/contacts`,
    contact: (buildingId: string, contactId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/contacts/${contactId}`,
    contactAttachments: (buildingId: string, contactId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/contacts/${contactId}/attachments`,
    amenities: (id: string) => `${API_BASE_URL}/api/buildings/${id}/amenities`,
    amenity: (buildingId: string, amenityId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/amenities/${amenityId}`,
    events: (id: string, from?: string, to?: string) => {
      const qs = [from && `from=${encodeURIComponent(from)}`, to && `to=${encodeURIComponent(to)}`].filter(Boolean).join('&');
      return `${API_BASE_URL}/api/buildings/${id}/events${qs ? `?${qs}` : ''}`;
    },
    event: (buildingId: string, eventId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/events/${eventId}`,
    infoSections: (id: string) => `${API_BASE_URL}/api/buildings/${id}/info-sections`,
    infoSection: (buildingId: string, sectionId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/info-sections/${sectionId}`,
    safety: (id: string) => `${API_BASE_URL}/api/buildings/${id}/safety`,
    safetySections: (id: string) => `${API_BASE_URL}/api/buildings/${id}/safety/sections`,
    safetySection: (buildingId: string, sectionId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/safety/sections/${sectionId}`,
    safetyDiagram: (id: string) => `${API_BASE_URL}/api/buildings/${id}/safety/diagram`,
    claim: (id: string) => `${API_BASE_URL}/api/buildings/${id}/claim`,
    contracts: (id: string) => `${API_BASE_URL}/api/buildings/${id}/contracts`,
    followersSearch: (id: string, q?: string) => `${API_BASE_URL}/api/buildings/${id}/followers/search${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    contract: (buildingId: string, contractId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/contracts/${contractId}`,
    contractFile: (buildingId: string, contractId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/contracts/${contractId}/file`,
    conciergeAsk: (id: string) => `${API_BASE_URL}/api/buildings/${id}/concierge/ask`,
    sentiment: (id: string) => `${API_BASE_URL}/api/buildings/${id}/sentiment`,
    operator: (id: string) => `${API_BASE_URL}/api/buildings/${id}/operator`,
    myLabel: (id: string) => `${API_BASE_URL}/api/buildings/${id}/my-label`,
    privacy: (id: string) => `${API_BASE_URL}/api/buildings/${id}/privacy`,
    accessList: (id: string) => `${API_BASE_URL}/api/buildings/${id}/access-list`,
    accessListEntry: (buildingId: string, entryId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/access-list/${entryId}`,
    checkins: (id: string) => `${API_BASE_URL}/api/buildings/${id}/checkins`,
  },
  operator: {
    buildings: (userId: string) => `${API_BASE_URL}/api/buildings/operator/${userId}/buildings`,
    members: (buildingId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/members`,
    setMemberLabel: (buildingId: string, userId: string) => `${API_BASE_URL}/api/buildings/${buildingId}/members/${userId}/label`,
  },
  search: {
    autocomplete: `${API_BASE_URL}/api/search/autocomplete`,
    placeDetails: `${API_BASE_URL}/api/search/place-details`,
    plusCode: `${API_BASE_URL}/api/search/plus-code`,
    sessionToken: `${API_BASE_URL}/api/search/session-token`,
  },
  profile: {
    getById: (id: string) => `${API_BASE_URL}/api/profile/${id}`,
  },
  contact: `${API_BASE_URL}/api/contact`,
  contracts: {
    list: `${API_BASE_URL}/api/contracts`,
    upload: `${API_BASE_URL}/api/contracts/upload`,
    delete: (id: string) => `${API_BASE_URL}/api/contracts/${id}`,
    file: (id: string) => `${API_BASE_URL}/api/contracts/${id}/file`,
    ask: `${API_BASE_URL}/api/contracts/ask`,
    usage: `${API_BASE_URL}/api/contracts/usage`,
    questions: `${API_BASE_URL}/api/contracts/questions`,
  },
  push: {
    subscribe: `${API_BASE_URL}/api/push/subscribe`,
    unsubscribe: `${API_BASE_URL}/api/push/subscribe`,
  },
  notifications: {
    list: `${API_BASE_URL}/api/notifications`,
    unreadCount: `${API_BASE_URL}/api/notifications/unread-count`,
    readAll: `${API_BASE_URL}/api/notifications/read-all`,
    clearAll: `${API_BASE_URL}/api/notifications/clear-all`,
    markRead: (id: string) => `${API_BASE_URL}/api/notifications/${id}/read`,
    delete: (id: string) => `${API_BASE_URL}/api/notifications/${id}`,
  },
  crm: {
    meta: `${API_BASE_URL}/api/crm/meta`,
    // Query string built by the caller from its filter state; the server
    // ignores anything it doesn't recognise and 400s on an unknown stage,
    // source or service rather than silently returning everything.
    leads: (query?: string) => `${API_BASE_URL}/api/crm/leads${query ? `?${query}` : ''}`,
    createLead: `${API_BASE_URL}/api/crm/leads`,
    lead: (id: string) => `${API_BASE_URL}/api/crm/leads/${id}`,
    activities: (leadId: string) => `${API_BASE_URL}/api/crm/leads/${leadId}/activities`,
    activity: (leadId: string, id: string) => `${API_BASE_URL}/api/crm/leads/${leadId}/activities/${id}`,
  },
  admin: {
    me: `${API_BASE_URL}/api/admin/me`,
    stats: `${API_BASE_URL}/api/admin/stats`,
    claims: `${API_BASE_URL}/api/admin/claims`,
    approveClaim: (claimId: string) => `${API_BASE_URL}/api/admin/claims/${claimId}/approve`,
    rejectClaim: (claimId: string) => `${API_BASE_URL}/api/admin/claims/${claimId}/reject`,
    users: (search?: string) => `${API_BASE_URL}/api/admin/users${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    updateUserRole: (userId: string) => `${API_BASE_URL}/api/admin/users/${userId}/role`,
    blockUser: (userId: string) => `${API_BASE_URL}/api/admin/users/${userId}/block`,
    updateBuildingTier: (buildingId: string) => `${API_BASE_URL}/api/admin/buildings/${buildingId}/tier`,
    deleteBuilding: (buildingId: string) => `${API_BASE_URL}/api/admin/buildings/${buildingId}`,
    listBuildings: `${API_BASE_URL}/api/admin/buildings`,
    featuredBuilding: `${API_BASE_URL}/api/admin/featured-building`,
    buildingPrivacy: (buildingId: string) => `${API_BASE_URL}/api/admin/buildings/${buildingId}/privacy`,
    buildingAccessList: (buildingId: string) => `${API_BASE_URL}/api/admin/buildings/${buildingId}/access-list`,
    buildingAccessEntry: (buildingId: string, entryId: string) => `${API_BASE_URL}/api/admin/buildings/${buildingId}/access-list/${entryId}`,
    flaggedPosts: `${API_BASE_URL}/api/admin/flagged`,
    draftReviews: `${API_BASE_URL}/api/admin/draft-reviews`,
    lowStarReviews: `${API_BASE_URL}/api/admin/low-star-reviews`,
    publishDraftReview: (postId: string) => `${API_BASE_URL}/api/admin/draft-reviews/${postId}/publish`,
    archiveDraftReview: (postId: string) => `${API_BASE_URL}/api/admin/draft-reviews/${postId}/archive`,
    pendingUsers: `${API_BASE_URL}/api/admin/pending-users`,
    approveUser: (userId: string) => `${API_BASE_URL}/api/admin/users/${userId}/approve`,
    blockUserApplication: (userId: string) => `${API_BASE_URL}/api/admin/users/${userId}/block-application`,
    deleteUser: (userId: string) => `${API_BASE_URL}/api/admin/users/${userId}`,
    posts: (page: number, limit: number) => `${API_BASE_URL}/api/admin/posts?page=${page}&limit=${limit}`,
    files: (page: number, limit: number) => `${API_BASE_URL}/api/admin/files?page=${page}&limit=${limit}`,
  },
};
